/**
 * Constantes del dominio de Órdenes: etiquetas legibles, reglas de transición y
 * el mapeo acción -> permiso/método HTTP.
 *
 * El gating de la UI usa los MISMOS permisos que exige la API (`progress/
 * explore_orders_api.md`, §7): `complete_orders`, `deliver_orders`, `edit_orders`
 * (anular/reactivar) y `all` (eliminar). Las reglas de transición replican las del
 * backend/SSR (`progress/explore_orders_ssr.md`, §D): entregar solo si está
 * completada y pagada; anular salvo entregada; reactivar solo anulada.
 */
import type {
  OrderAction,
  OrderStatus,
  PaymentStatus,
} from "./types";

/** Etiquetas legibles de los estados de proceso (UI en español). */
export const STATUS_LABEL: Record<OrderStatus, string> = {
  pendiente: "Pendiente",
  completado: "En proceso",
  entregado: "Entregada",
  anulada: "Anulada",
};

/** Etiquetas legibles de los estados de pago. */
export const PAYMENT_LABEL: Record<PaymentStatus, string> = {
  pendiente_de_pago: "Pendiente de pago",
  pagada_para_despacho: "Pagada para despacho",
  abonado: "Abonado",
  pagado: "Pagado",
  orden_a_credito: "Orden a crédito",
};

/**
 * Estados de pago que el backend considera "pagado" para habilitar la entrega
 * (`progress/explore_orders_ssr.md`, §D: payment_status ∈ {pagado,
 * pagada_para_despacho, orden_a_credito}).
 */
const PAID_FOR_DELIVERY: ReadonlySet<PaymentStatus> = new Set<PaymentStatus>([
  "pagado",
  "pagada_para_despacho",
  "orden_a_credito",
]);

/** `true` si la orden está pagada lo suficiente para poder entregarse. */
export function isPaidForDelivery(payment: PaymentStatus): boolean {
  return PAID_FOR_DELIVERY.has(payment);
}

/** Permiso que exige cada acción de transición (espejo exacto de la API). */
export const ACTION_PERMISSION: Record<OrderAction, string> = {
  complete: "complete_orders",
  deliver: "deliver_orders",
  cancel: "edit_orders",
  reactivate: "edit_orders",
  delete: "all",
};

/** Etiqueta del botón de cada acción. */
export const ACTION_LABEL: Record<OrderAction, string> = {
  complete: "Completar",
  deliver: "Entregar",
  cancel: "Anular",
  reactivate: "Reactivar",
  delete: "Eliminar",
};

/**
 * Reglas de transición permitidas por estado (independientes del permiso).
 *
 *  - `pendiente`  -> completar, anular, eliminar
 *  - `completado` -> entregar (solo si pagada), anular, eliminar
 *  - `entregado`  -> (solo eliminar; no se anula una entregada)
 *  - `anulada`    -> reactivar, eliminar
 *
 * `deliver` se incluye en `completado` pero su disponibilidad real depende
 * además del estado de pago (`isPaidForDelivery`), que se evalúa al pintar.
 */
export const ALLOWED_ACTIONS: Record<OrderStatus, readonly OrderAction[]> = {
  pendiente: ["complete", "cancel", "delete"],
  completado: ["deliver", "cancel", "delete"],
  entregado: ["delete"],
  anulada: ["reactivate", "delete"],
};

/**
 * Texto de confirmación de cada acción (las acciones destructivas/irreversibles
 * piden confirmación explícita antes de llamar a la API).
 */
export const ACTION_CONFIRM: Record<OrderAction, string> = {
  complete: "¿Marcar esta orden como completada?",
  deliver: "¿Marcar esta orden como entregada?",
  cancel: "¿Anular esta orden?",
  reactivate: "¿Reactivar esta orden anulada?",
  delete: "¿Eliminar esta orden de forma permanente? Esta acción no se puede deshacer.",
};

/**
 * Diccionario de mensajes legibles por `error_code` de la API de órdenes
 * (`progress/explore_orders_api.md`, §6). Si llega un código no listado, se cae
 * al `message` del propio `ApiError` (que el backend ya da en español).
 *
 * Incluye tanto los códigos de transición (invalid_state, already_cancelled, …)
 * como los de validación de alta/edición (missing_fields, duplicate_order_number,
 * invalid_amount(_format), abono_*, *_required, invalid_invoice/delivery_date),
 * para que el formulario (#48) y las acciones (#47) compartan la traducción.
 */
export const ORDER_ERROR_MESSAGE: Record<string, string> = {
  // Transiciones de estado.
  invalid_state: "La orden no está en un estado válido para esta acción.",
  already_cancelled: "La orden ya estaba anulada.",
  delivered: "No se puede anular una orden ya entregada.",
  not_cancelled: "Solo se pueden reactivar órdenes anuladas.",
  not_found: "La orden no existe o pertenece a otra sede.",
  forbidden: "No tienes permiso para realizar esta acción.",
  bad_request: "Falta seleccionar la sede.",
  // Alta y edición (validación del cuerpo).
  missing_fields: "Completa todos los campos obligatorios.",
  duplicate_order_number: "El número de orden ya existe en esta sede.",
  invalid_amount: "El monto debe ser positivo.",
  invalid_amount_format: "El monto debe ser un número válido.",
  abono_invalid: "El monto abonado debe ser mayor a 0.",
  abono_too_high: "El monto abonado no puede ser mayor o igual al total de la orden.",
  order_number_required: "El número de orden es obligatorio.",
  customer_name_required: "El nombre del cliente es obligatorio.",
  attendant_required: "El atendido es obligatorio.",
  sale_amount_required: "El monto es obligatorio.",
  invalid_invoice_date: "La fecha de ingreso no es válida.",
  invalid_delivery_date: "La fecha de entrega no es válida.",
  validation_error: "Revisa los datos del formulario.",
};

/**
 * Campos editables que el backend protege con un permiso granular en `PATCH`
 * (`progress/explore_orders_api.md`, §7.2): si el actor NO tiene el permiso, el
 * service ignora ese campo. La UI replica la regla mostrándolo solo-lectura y
 * **sin enviarlo**. `order_type` no tiene permiso granular (solo `edit_orders` +
 * que la sede permita láser), así que no entra en este mapa.
 */
export const FIELD_EDIT_PERMISSION: Record<string, string> = {
  order_number: "edit_order_number",
  customer_name: "edit_customer_name",
  attendant: "edit_attendant",
  sale_amount: "edit_sale_amount",
  invoice_date: "edit_invoice_date",
  delivery_date: "edit_delivery_date",
  delivery_time: "edit_delivery_time",
  payment_status: "edit_payment_status",
  description: "edit_description",
};

/**
 * Mapeo de `error_code` de la API al campo del formulario al que pertenece, para
 * anclar el mensaje a su input (en vez de mostrarlo solo a nivel de formulario).
 * Los códigos no listados (p. ej. `missing_fields`, `validation_error`) se
 * muestran como error general del formulario.
 */
export const ERROR_CODE_FIELD: Record<string, keyof typeof FIELD_EDIT_PERMISSION | "amount_paid"> = {
  duplicate_order_number: "order_number",
  order_number_required: "order_number",
  customer_name_required: "customer_name",
  attendant_required: "attendant",
  sale_amount_required: "sale_amount",
  invalid_amount: "sale_amount",
  invalid_amount_format: "sale_amount",
  invalid_invoice_date: "invoice_date",
  invalid_delivery_date: "delivery_date",
  abono_invalid: "amount_paid",
  abono_too_high: "amount_paid",
};

/** Etiquetas legibles de cada campo del formulario (UI en español). */
export const FIELD_LABEL = {
  order_number: "Número de orden",
  customer_name: "Cliente",
  attendant: "Atendido por",
  sale_amount: "Monto de venta",
  invoice_date: "Fecha de ingreso",
  delivery_date: "Fecha de entrega",
  delivery_time: "Hora de entrega",
  payment_status: "Estado de pago",
  amount_paid: "Monto abonado",
  description: "Descripción",
  order_type: "Tipo de orden",
} as const;
